// Shared "how do we render one audit-log field diff as text" logic — originally local to
// AuditLogPage.tsx, extracted so the per-issue History tab/section (IssueDetailDialog.tsx,
// IssueLinkPage.tsx) can render the exact same diff format instead of re-implementing it.
import { formatDdMmYyyy } from './date.util';

const ISO_DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

// issue.service.ts's `formatWorkLogForAudit()` builds a single label string like
// "2h on 2026-09-08 — note" with the date embedded mid-string, so fmtVal()'s own ISO-date
// detection (which only reformats a value that IS entirely an ISO date, not one that merely
// contains one) can't reach it. This pulls just that date segment out and reformats it to the
// app's DD-MM-YYYY convention, leaving the rest of the label untouched.
const WORK_LOG_LABEL = /^(\d+(?:\.\d+)?h on )(\d{4}-\d{2}-\d{2})( — .+)$/;

function fmtWorkLogLabel(v: string): string {
  const match = WORK_LOG_LABEL.exec(v);
  if (!match) return v;
  return `${match[1]}${formatDdMmYyyy(match[2]!)}${match[3]}`;
}

export function fmtVal(v: unknown): string {
  if (v === null || v === undefined) return '(none)';
  if (Array.isArray(v)) {
    const arr = v as unknown[];
    return arr.length === 0 ? '(none)' : arr.join(', ');
  }
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'string') {
    if (ISO_DATE_ONLY.test(v)) return formatDdMmYyyy(v);
    return v.length > 40 ? `${v.slice(0, 40)}…` : v;
  }
  // A structured `{ id, name }` reference (e.g. Project Lead's audit diff) — render the name,
  // not `[object Object]`. Every other relation field in this app's audit diffs is already
  // resolved to a plain name string server-side; this is the one exception.
  if (typeof v === 'object' && v !== null && 'name' in v && typeof (v as { name: unknown }).name === 'string') {
    return (v as { name: string }).name;
  }
  return String(v);
}

export function fmtArrayDiff(from: unknown[], to: unknown[]): string {
  const fromSet = new Set(from.map(String));
  const toSet = new Set(to.map(String));
  const added = to.map(String).filter((x) => !fromSet.has(x));
  const removed = from.map(String).filter((x) => !toSet.has(x));
  const parts: string[] = [];
  if (added.length > 0) parts.push(`+${added.join(', ')}`);
  if (removed.length > 0) parts.push(`−${removed.join(', ')}`);
  return parts.length > 0 ? parts.join('  ') : 'No change';
}

// project-staffing.service.ts's `staffingDatesEdited` old/new are both a structured
// `{ userId, name, startDate, endDate }` snapshot of the same staffing row — the person never
// changes for this action, only their Start/End Date. fmtVal()'s generic `{ name }` handling
// would render "Name → Name" (the actual date change invisible), so this key gets its own
// formatter instead of falling through to the generic one. Kept local to this one key so
// Project Lead/Project Manager's existing `{ id, name }` rendering (via fmtVal) is untouched.
function fmtStaffingDatesEdited(from: unknown, to: unknown): string {
  const f = (from ?? {}) as { name?: string; startDate?: string; endDate?: string | null };
  const t = (to ?? {}) as { name?: string; startDate?: string; endDate?: string | null };

  const parts: string[] = [];
  if (f.startDate !== t.startDate) parts.push(`Start: ${fmtVal(f.startDate)} → ${fmtVal(t.startDate)}`);
  if (f.endDate !== t.endDate) parts.push(`End: ${fmtVal(f.endDate)} → ${fmtVal(t.endDate)}`);

  const who = t.name ?? f.name;
  const detail = parts.length > 0 ? parts.join(', ') : 'No change';
  return who ? `${who} — ${detail}` : detail;
}

// Shared by staffingAllocation/staffingDestaffed in fmtChange() below — same "<person> —
// <field>: old → new" shape as fmtStaffingDatesEdited() above, generalized to a single
// caller-provided field instead of the fixed startDate/endDate pair.
function fmtStaffingSingleFieldChange(from: unknown, to: unknown, field: string, fieldLabel: string): string {
  const f = (from ?? {}) as Record<string, unknown> & { name?: string };
  const t = (to ?? {}) as Record<string, unknown> & { name?: string };
  const who = t.name ?? f.name;
  const detail = `${fieldLabel}: ${fmtVal(f[field])} → ${fmtVal(t[field])}`;
  return who ? `${who} — ${detail}` : detail;
}

// risk.service.ts/dependency.service.ts/escalation.service.ts's `linkedIssues`/
// `linkedDependencies`/`linkedRisks`, and change-request.service.ts's `changeDetailLines`/
// `riskAssessmentLines`, are arrays of plain descriptive labels (e.g. "ACME-ISSUE-001 Fix the
// thing", or a Change Detail/Risk line's own free text) that can themselves contain a literal
// "-"/"—" character, which made the generic fmtArrayDiff()'s bare "−" removal prefix read as
// ambiguous — easy to mistake for part of the label rather than the diff marker. Spelling out
// "(removed)" per item instead matches this app's other delete-shaped diffs (workLog, the
// generic Delete branch below). Kept local to these keys so permissions/roles' existing +/−
// array format (fmtArrayDiff) is untouched.
function fmtLinkedEntitiesDiff(from: unknown, to: unknown): string {
  const fromArr = Array.isArray(from) ? (from as unknown[]).map(String) : [];
  const toArr = Array.isArray(to) ? (to as unknown[]).map(String) : [];
  const fromSet = new Set(fromArr);
  const toSet = new Set(toArr);
  const added = toArr.filter((x) => !fromSet.has(x));
  const removed = fromArr.filter((x) => !toSet.has(x));
  const parts = [...added, ...removed.map((x) => `${x} (removed)`)];
  return parts.length > 0 ? parts.join(', ') : 'No change';
}

// AuditService.log() always merges Project context in as the last key of `changes` before
// saving, but Postgres jsonb does not preserve object key insertion order — it re-serializes
// keys by length then lexically, so "project" can come back ahead of a longer real-field key
// like "priority". Sorting `project` to the end at render time keeps the actual field changes
// listed first, with Project shown last as context, regardless of storage order.
export function orderChangeKeys(changes: Record<string, unknown>): string[] {
  const keys = Object.keys(changes);
  return [...keys.filter((k) => k !== 'project'), ...keys.filter((k) => k === 'project')];
}

// User-facing overrides for change-diff keys whose raw field name isn't a good display label —
// the stored key (e.g. `isFunded`) is never touched, only how it's rendered here. Extend this
// map for a new field rather than adding another one-off condition in fmtChange() below.
const FIELD_LABELS: Record<string, string> = {
  isActive: 'Status',
  // Project's Exemption flow flag (§5d) — the property/DTO/DB column stays `isFunded`; only the
  // Audit Log's displayed label reads as "Is Funding Approved", matching the Contractual &
  // Finance UI's own renamed field label (ProjectOverviewTab.tsx/ProjectFinanceDialog.tsx).
  isFunded: 'Is Funding Approved',
  // Project's Tech Lead — the property/DTO/DB column stays `projectLead`; only the Audit Log's
  // displayed label reads as "Tech Lead", matching the renamed UI field label
  // (ProjectOverviewTab.tsx/ProjectFormDialog.tsx/ProjectListPage.tsx/etc.).
  projectLead: 'Tech Lead',
  // change-request.service.ts's changeDetailComments — a dedicated key (sibling to
  // changeDetailLines) for Change Detail line Comments edits, kept separate so the diff reads
  // clearly apart from changeDetail/effort.
  changeDetailComments: 'Change Detail Comments',
};

export function fmtChange(key: string, from: unknown, to: unknown): string {
  // Special-case well-known field names for nicer labels
  const label = FIELD_LABELS[key] ?? key;

  // project-staffing.service.ts's `staffingAllocation`/`staffingDestaffed` old/new are both a
  // structured `{ userId, name, ... }` snapshot of the same staffing row — the staffed person
  // doesn't change for either action, only one other field does (`allocatedHoursPerWeek` /
  // `endDate`). Same shape/reasoning as fmtStaffingDatesEdited() below, just for a single
  // always-present field instead of two conditionally-shown ones — rendered the same way:
  // "<person> — <field>: old → new", reusing fmtVal() for the actual value formatting so its
  // `(none)` null handling and date auto-formatting (for `endDate`) apply for free.
  if (key === 'staffingAllocation') {
    return `${label}: ${fmtStaffingSingleFieldChange(from, to, 'allocatedHoursPerWeek', 'Hours/Week')}`;
  }
  if (key === 'staffingDestaffed') {
    return `${label}: ${fmtStaffingSingleFieldChange(from, to, 'endDate', 'End Date')}`;
  }

  // See fmtWorkLogLabel()'s comment — the date embedded in this label needs its own reformatting
  // pass since it's part of a larger string, not a bare ISO date value fmtVal() would catch.
  // Mirrors the generic Create/Delete/plain-value shape below (just using fmtLabel in place of
  // fmtVal), so a create still shows only the new label rather than "(none) → ...".
  if (key === 'workLog') {
    const fmtLabel = (v: unknown) => (typeof v === 'string' ? fmtWorkLogLabel(v) : fmtVal(v));
    if (from === null || from === undefined) return `${label}: ${fmtLabel(to)}`;
    if (to === null || to === undefined) return `${label}: ${fmtLabel(from)} (removed)`;
    return `${label}: ${fmtLabel(from)} → ${fmtLabel(to)}`;
  }

  // See fmtStaffingDatesEdited()'s comment — this one key needs its own format, every other
  // structured-object change (Project Lead/Project Manager's `{ id, name }`, etc.) keeps using
  // the generic handling below via fmtVal().
  if (key === 'staffingDatesEdited') {
    return `${label}: ${fmtStaffingDatesEdited(from, to)}`;
  }

  if (
    key === 'linkedIssues' || key === 'linkedDependencies' || key === 'linkedRisks' ||
    key === 'changeDetailLines' || key === 'riskAssessmentLines'
  ) {
    return `${label}: ${fmtLinkedEntitiesDiff(from, to)}`;
  }

  // Boolean (e.g. isActive)
  if (typeof from === 'boolean' || typeof to === 'boolean') {
    if (key === 'isActive') {
      return from ? 'Active → Deactivated' : 'Deactivated → Active';
    }
    return `${label}: ${fmtVal(from)} → ${fmtVal(to)}`;
  }

  // Arrays (e.g. permissions, roles)
  if (Array.isArray(from) && Array.isArray(to)) {
    return `${label}: ${fmtArrayDiff(from as unknown[], to as unknown[])}`;
  }

  // Create (null → value)
  if (from === null || from === undefined) {
    return `${label}: ${fmtVal(to)}`;
  }

  // Delete (value → null)
  if (to === null || to === undefined) {
    return `${label}: ${fmtVal(from)} (removed)`;
  }

  // Plain value change
  return `${label}: ${fmtVal(from)} → ${fmtVal(to)}`;
}
